/**
 * Auth Context - Manage authentication state + organization role
 */

import { QueryClient, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

type UserRole = 'owner' | 'admin' | 'member';

export type OrgLookupResult =
  | { status: 'ok'; organizationId: string; role: UserRole }
  | { status: 'none' }
  | { status: 'ambiguous' }
  | { status: 'error' };

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  organizationId: string | null;
  role: UserRole | null;
  isAdmin: boolean;
  organizationStatus: OrgLookupResult['status'] | null;
  signOut: () => Promise<void>;
  retryOrganizationLookup: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function loadOrgRole(userId: string): Promise<OrgLookupResult> {
  const { data, error } = await supabase
    .from('organization_users')
    .select('organization_id, role')
    .eq('user_id', userId)
    .order('created_at', { ascending: true, nullsFirst: false })
    .order('organization_id', { ascending: true })
    // Fetch up to 2 rows without `.single()` so we can tell "no membership"
    // apart from "ambiguous membership" and fail closed on the latter rather
    // than picking a row. The server currently has no shared
    // selected-organization contract, so choosing one row client-side could
    // disagree with API authorization.
    .limit(2);

  if (error) return { status: 'error' };
  if (!data || data.length === 0) return { status: 'none' };
  if (data.length >= 2) return { status: 'ambiguous' };

  const row = data[0];
  if (!row) return { status: 'error' };
  return { status: 'ok', organizationId: row.organization_id, role: row.role as UserRole };
}

/**
 * Whether a newly resolved organization lookup represents a genuine change
 * in authorization compared to what is currently held. A transient `error`
 * is never treated as a change (the existing authorization is preserved), so
 * callers must not run cache-clearing side effects for it.
 */
function computeAuthorizationChanged(
  previous: { organizationId: string; role: UserRole } | null,
  next: OrgLookupResult,
): boolean {
  if (next.status === 'error') return false;
  if (next.status === 'ok') {
    return (
      previous?.organizationId !== next.organizationId ||
      previous?.role !== next.role
    );
  }
  // 'none' or 'ambiguous': a change only if we previously held a usable
  // authorization that is now gone.
  return previous !== null;
}

function waitForMutationsToSettle(
  queryClient: QueryClient,
  signal: AbortSignal,
): Promise<void> {
  if (signal.aborted || queryClient.isMutating() === 0) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    let settled = false;
    let unsubscribe: () => void = () => undefined;

    const finish = () => {
      if (settled) return;
      settled = true;
      unsubscribe();
      signal.removeEventListener('abort', finish);
      resolve();
    };
    const check = () => {
      if (signal.aborted || queryClient.isMutating() === 0) {
        finish();
      }
    };

    unsubscribe = queryClient.getMutationCache().subscribe(check);
    signal.addEventListener('abort', finish, { once: true });
    check();
  });
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [organizationStatus, setOrganizationStatus] =
    useState<OrgLookupResult['status'] | null>(null);
  const identityRef = useRef<string | null>(null);
  const authorizationRef = useRef<{
    organizationId: string;
    role: UserRole;
  } | null>(null);
  const transitionRef = useRef(0);
  const transitionAbortRef = useRef<AbortController | null>(null);

  const applyOrgLookup = useCallback((orgRole: OrgLookupResult): void => {
    setOrganizationStatus(orgRole.status);

    if (orgRole.status === 'ok') {
      authorizationRef.current = {
        organizationId: orgRole.organizationId,
        role: orgRole.role,
      };
      setOrganizationId(orgRole.organizationId);
      setRole(orgRole.role);
      return;
    }

    if (orgRole.status === 'error') {
      // Transient failure: keep whatever authorization is already held
      // rather than tearing down a working session.
      return;
    }

    // 'none' or 'ambiguous': fail closed, there is no usable authorization.
    authorizationRef.current = null;
    setOrganizationId(null);
    setRole(null);
  }, []);

  const handleSession = useCallback(async (newSession: Session | null): Promise<void> => {
    const nextIdentity = newSession?.user.id ?? null;
    const identityChanged = identityRef.current !== nextIdentity;

    if (!identityChanged) {
      setSession(newSession);
      setUser(newSession?.user ?? null);

      if (!nextIdentity) {
        authorizationRef.current = null;
        setOrganizationId(null);
        setRole(null);
        setOrganizationStatus(null);
        setLoading(false);
        return;
      }

      transitionAbortRef.current?.abort();
      const transitionAbort = new AbortController();
      transitionAbortRef.current = transitionAbort;
      const transition = ++transitionRef.current;

      const orgRole = await loadOrgRole(nextIdentity);

      if (
        transitionRef.current !== transition ||
        identityRef.current !== nextIdentity
      ) {
        return;
      }

      const authorizationChanged = computeAuthorizationChanged(
        authorizationRef.current,
        orgRole,
      );

      if (authorizationChanged) {
        while (queryClient.isMutating() > 0) {
          // Wait for every in-flight mutation to reach a terminal state
          // before publishing the new authorization. Re-check after every
          // wake-up so a mutation queued in the same turn cannot slip
          // through.
          await waitForMutationsToSettle(queryClient, transitionAbort.signal);

          if (
            transitionAbort.signal.aborted ||
            transitionRef.current !== transition ||
            identityRef.current !== nextIdentity
          ) {
            return;
          }
        }

        // Every mutation is terminal now, so clear both query and mutation
        // caches before publishing the new authorization.
        queryClient.clear();
      }

      applyOrgLookup(orgRole);
      setLoading(false);
      return;
    }

    transitionAbortRef.current?.abort();
    const transitionAbort = new AbortController();
    transitionAbortRef.current = transitionAbort;
    const transition = ++transitionRef.current;
    identityRef.current = nextIdentity;
    authorizationRef.current = null;

    // The QueryClient outlives AuthProvider state. Clear it before publishing a
    // different identity so cached tenant data can never bridge auth sessions.
    queryClient.clear();
    setLoading(true);
    setSession(newSession);
    setUser(newSession?.user ?? null);
    setOrganizationId(null);
    setRole(null);
    setOrganizationStatus(null);

    if (newSession?.user) {
      const orgRole = await loadOrgRole(newSession.user.id);

      if (
        transitionRef.current !== transition ||
        identityRef.current !== nextIdentity
      ) {
        return;
      }

      applyOrgLookup(orgRole);
    }

    setLoading(false);
  }, [applyOrgLookup, queryClient]);

  useEffect(() => {
    let disposed = false;
    let authEventSeen = false;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      authEventSeen = true;
      if (!disposed) {
        void handleSession(nextSession);
      }
    });

    void supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      if (!disposed && !authEventSeen) {
        void handleSession(initialSession);
      }
    });

    return () => {
      disposed = true;
      transitionAbortRef.current?.abort();
      transitionRef.current += 1;
      subscription.unsubscribe();
    };
  }, [handleSession]);

  const retryOrganizationLookup = useCallback(async (): Promise<void> => {
    const currentUserId = identityRef.current;
    if (!currentUserId) return;

    transitionAbortRef.current?.abort();
    const transitionAbort = new AbortController();
    transitionAbortRef.current = transitionAbort;
    const transition = ++transitionRef.current;

    const orgRole = await loadOrgRole(currentUserId);

    if (
      transitionRef.current !== transition ||
      identityRef.current !== currentUserId
    ) {
      return;
    }

    const authorizationChanged = computeAuthorizationChanged(
      authorizationRef.current,
      orgRole,
    );

    if (authorizationChanged) {
      while (queryClient.isMutating() > 0) {
        await waitForMutationsToSettle(queryClient, transitionAbort.signal);

        if (
          transitionAbort.signal.aborted ||
          transitionRef.current !== transition ||
          identityRef.current !== currentUserId
        ) {
          return;
        }
      }

      queryClient.clear();
    }

    applyOrgLookup(orgRole);
  }, [applyOrgLookup, queryClient]);

  const signOut = async (): Promise<void> => {
    transitionAbortRef.current?.abort();
    transitionRef.current += 1;
    identityRef.current = null;
    authorizationRef.current = null;
    queryClient.clear();
    setSession(null);
    setUser(null);
    setOrganizationId(null);
    setRole(null);
    setOrganizationStatus(null);
    setLoading(false);
    await supabase.auth.signOut();
  };

  const isAdmin = role === 'owner' || role === 'admin';

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        loading,
        organizationId,
        role,
        isAdmin,
        organizationStatus,
        signOut,
        retryOrganizationLookup,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
